import { useAuth0 } from "@auth0/auth0-react";
import { Spinner } from "@heroui/react";
import { useEffect } from "react";
import { Navigate } from "react-router";
import { useSession } from "@/services/auth";
import { SessionRecovery } from "@/components/session-recovery";

/**
 * URL-addressable Auth0 login entry point.
 *
 * Auth0 login is normally an in-app `loginWithRedirect()` call with no URL.
 * The standalone marketing site can only deep-link by URL, so `/sign-in` and
 * `/sign-up` mount this screen, which kicks off the Auth0 redirect on load.
 * (The Clerk provider has its own hosted `/sign-in` pages instead.)
 */
export default function AuthRedirectScreen({
  screenHint,
}: {
  screenHint?: "signup";
}) {
  const { loginWithRedirect } = useAuth0();
  const { isLoading, isLoggedIn, sessionError } = useSession();

  useEffect(() => {
    if (isLoading || isLoggedIn || sessionError) return;
    void loginWithRedirect(
      screenHint
        ? { authorizationParams: { screen_hint: screenHint } }
        : undefined
    );
  }, [isLoading, isLoggedIn, sessionError, loginWithRedirect, screenHint]);

  if (!isLoading && sessionError) return <SessionRecovery />;

  if (isLoggedIn) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-default-50">
      <Spinner size="lg" color="primary" />
    </div>
  );
}
